import React from "react";

import { FillingType } from "../../../../api/FastAPI.types";
import { DURATION_UNITS } from "../../../../utils/durations";
import DataTableColumnHeader from "../../../dataTable/DataTableColumnHeader";
import {
  dateTimeColumn,
  durationColumn,
  lazyActionsColumn,
} from "../../../dataTable/dataTableColumns";
import DataTableLinkCell from "../../../dataTable/DataTableLinkCell";
import { timeslotPath } from "../../../routes/routes";

import type { TFunction } from "i18next";

import type { TimeslotListItem } from "../../../../api/FastAPI.types";
import type {
  DataTableColumnDef,
  RowAction,
} from "../../../dataTable/dataTable.types";

interface TimeslotColumnsOptions {
  t: TFunction;
  channelId: string;
  loadActions: (timeslot: TimeslotListItem) => Promise<RowAction[]>;
}

const PLAY_MODE_LIST = [FillingType.Loop, FillingType.SingleRun];

export function getTimeslotHref(
  channelId: string,
  timeslot: TimeslotListItem,
): string {
  return timeslotPath(channelId, timeslot.uuid);
}

export function timeslotColumns({
  t,
  channelId,
  loadActions,
}: TimeslotColumnsOptions): DataTableColumnDef<TimeslotListItem>[] {
  return [
    {
      accessorKey: "color",
      header: () => <span className="sr-only">{t("color")}</span>,
      enableSorting: false,
      meta: { label: t("color"), width: "28px" },
      cell: ({ row }) => (
        <span
          aria-hidden="true"
          className="block h-8 w-1 rounded-full"
          data-testid="timeslot-color"
          style={{ backgroundColor: row.original.color }}
        />
      ),
    },
    {
      accessorKey: "name",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("timeslotName")} />
      ),
      meta: { searchable: true, label: t("timeslotName") },
      cell: ({ row }) => (
        <DataTableLinkCell href={getTimeslotHref(channelId, row.original)}>
          {row.original.name}
        </DataTableLinkCell>
      ),
    },
    durationColumn<TimeslotListItem>(
      "duration_ms",
      t("duration"),
      DURATION_UNITS.MILLISECONDS,
      "humanised",
      { width: "15%", minWidth: 110, align: "center" },
    ),
    {
      accessorKey: "type",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("playMode")} />
      ),
      enableSorting: false,
      meta: {
        label: t("playMode"),
        width: "15%",
        minWidth: 145,
        align: "center",
        filterOptions: PLAY_MODE_LIST.map((mode) => ({
          value: mode,
          label: t(mode),
        })),
      },
      cell: ({ row }) => String(t(row.original.type)),
    },
    dateTimeColumn<TimeslotListItem>("updated_at", t("update_date")),
    lazyActionsColumn<TimeslotListItem>(
      loadActions,
      (timeslot) => timeslot.name,
    ),
  ];
}
